/**
 * A report figure with a "Photo | 3D" switch.
 *
 * The photo is the figure; the 3D view is an optional illustration of the same
 * readings, posed by the measured angles with the out-of-range areas marked on
 * the skin (lib/report/body3dModel). It lives in the same place as the photo,
 * so the report keeps one layout and one set of figure numbers.
 *
 *   - Photo is the default, and the only thing that prints: the switch and the
 *     3D view are screen-only, and in print the photo is always shown (so the
 *     print path's image settling is unchanged).
 *   - three.js and the body model load only when someone presses 3D.
 *   - On a browser without WebGL 2, or when there is nothing to show (no
 *     figure, or no photo to switch from — the caller passes figure={null}),
 *     the figure is the photo alone, exactly as before.
 */

import React, { Suspense, lazy, useState } from 'react';
import { canRender3D } from '../../lib/body3d/support';

const Body3DViewer = lazy(() => import('./Body3DViewer'));

const CAPTION = {
  posture: 'The figure is posed from the angles measured in this assessment. Coloured areas mark readings outside the normal range.',
  rearfoot: 'Feet and ankles from behind, posed from the measured heel and shin angles. Each heel is marked in its grade colour.',
};

export function FigureWith3D({ figure, kind, camera, view, children }) {
  const [mode, setMode] = useState('photo');
  if (!children || !figure || !canRender3D()) return children ?? null;
  const is3d = mode === '3d';
  return (
    <div className={`fig3d${is3d ? ' is-3d' : ''}`} data-testid={`fig3d-${kind}`}>
      <div className="fig3d-switch no-print" role="group" aria-label="Show the figure as">
        <button type="button" aria-pressed={!is3d} onClick={() => setMode('photo')}>Photo</button>
        <button type="button" aria-pressed={is3d} onClick={() => setMode('3d')}>3D</button>
      </div>
      <div className="fig3d-photo">{children}</div>
      {is3d && (
        <figure className="fig3d-model no-print">
          <div className="fighead">
            <span className="figtitle">3D figure</span>
            {view && <span className="figview">{view}</span>}
          </div>
          <Suspense fallback={<div className="body3d-stage"><p className="body3d-msg">Loading the 3D figure…</p></div>}>
            <Body3DViewer kind={kind} camera={camera} figure={figure} />
          </Suspense>
          <figcaption>
            {CAPTION[kind]} An illustration of the findings in this report, not a scan of the patient.
          </figcaption>
        </figure>
      )}
    </div>
  );
}

/** Screen styles for the switch and the 3D figure; the print rules keep the photo. */
export const FIG3D_CSS = `
.clinical .fig3d { position:relative; }
.clinical .fig3d-switch { display:flex; justify-content:flex-end; gap:0; margin:0 0 6px; }
.clinical .fig3d-switch button { font:700 8pt Arial,Helvetica,sans-serif; color:#526774; background:#fff; border:1px solid #d9e4e7; padding:5px 12px; cursor:pointer; }
.clinical .fig3d-switch button:first-child { border-radius:7px 0 0 7px; }
.clinical .fig3d-switch button:last-child { border-radius:0 7px 7px 0; border-left:0; }
.clinical .fig3d-switch button[aria-pressed="true"] { background:#0b2434; border-color:#0b2434; color:#fff; }
.clinical .fig3d-switch button:focus-visible { outline:2px solid #19c7c3; outline-offset:2px; }
.clinical .fig3d.is-3d .fig3d-photo { display:none; }
.clinical .body3d-stage { position:relative; height:96mm; background:radial-gradient(ellipse at 50% 40%, #f3f6f7 0%, #dfe7eb 75%); overflow:hidden; }
.clinical .body3d-rearfoot .body3d-stage { height:80mm; }
.clinical .body3d-gl, .clinical .body3d-gl canvas { position:absolute; inset:0; width:100%; height:100%; display:block; }
.clinical .body3d-gl canvas { touch-action:none; cursor:grab; }
.clinical .body3d-msg { position:absolute; inset:0; display:flex; align-items:center; justify-content:center; margin:0; font-size:8pt; color:#61737c; }
.clinical .body3d-pins { position:absolute; inset:0; pointer-events:none; }
.clinical .body3d-pin { position:absolute; transform:translate(-50%, calc(-100% - 7px)); pointer-events:auto; white-space:nowrap; font:700 7pt Arial,Helvetica,sans-serif; color:#fff; background:var(--c); border:0; border-radius:999px; padding:3px 8px; box-shadow:0 2px 6px rgba(11,36,52,.22); cursor:pointer; transition:opacity .2s; }
.clinical .body3d-pin b { font-weight:400; opacity:.92; }
.clinical .body3d-pin::after { content:""; position:absolute; left:50%; bottom:-4px; transform:translateX(-50%); border:4px solid transparent; border-bottom:0; border-top-color:var(--c); }
.clinical .body3d-pin.dim { opacity:.35; }
.clinical .body3d-bar { display:flex; justify-content:space-between; align-items:center; gap:8px; padding:6px 10px; font-size:7pt; color:#61737c; border-top:1px solid #e6ecef; }
.clinical .body3d-bar button { font:700 7pt Arial,Helvetica,sans-serif; color:#087f7f; background:none; border:0; cursor:pointer; padding:2px 0; }
@media print {
  .clinical .fig3d .fig3d-photo { display:block !important; }
}
`;
